import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Label, Input } from '@shared/components';
import { toMinorUnits } from '../services/schedulePreview';
import { formatMinorAmount } from '../utils/formatScheduleCurrency';
import { buildDepositAmountSchema, type DepositAmountFormData } from '../schemas/DepositInputSchema';

const DEBOUNCE_MS = 350;

interface DepositInputProps {
    /** The product's configured `min_deposit_amount`, minor units. `0`/`undefined` means no
     *  configured minimum, and an explicit £0 is then accepted as "no deposit". */
    minDepositMinor: number;
    /** The upper deposit bound, minor units (D5). A supplied deposit must be strictly less than this. Usually the quote total; for `item_based_with_deposit` (#618) the caller passes the first cumulative collection target instead. */
    maxDepositMinor: number;
    /** Names `maxDepositMinor` in the rejection message (e.g. "the first collection target"). Defaults to "the quote total" — existing callers are unaffected. */
    maxDepositLabel?: string;
    /**
     * When the binding ceiling is an INCLUSIVE cap (#621 — the product's
     * configured per-method deposit maximum), the raw inclusive amount to
     * DISPLAY in the hint and rejection message (e.g. 10000 / £100.00). The
     * `>=` comparison still uses the exclusive `maxDepositMinor` (`cap + 1`),
     * so a deposit equal to the cap is accepted while the salesperson is shown
     * the true cap, not the +1 exclusive value. Omit for the quote-total and
     * first-collection-target bounds, which display `maxDepositMinor` as today.
     */
    inclusiveMaxDisplayMinor?: number;
    currency: string;
    /** Current committed deposit, minor units — controlled from the parent (`PaymentStep`'s in-progress local state), so a quote/method reset is reflected here without this component owning that lifecycle itself. */
    valueMinor?: number;
    /** Fired ~350ms (D4) after the user stops typing a value that passes validation. Fired with `undefined` when the field is cleared. Never fired for an invalid value — the caller's live preview params are only ever updated with a value that has already passed the client-side contract rules. */
    onDebouncedChange: (depositMinor: number | undefined) => void;
    debounceMs?: number;
}

/**
 * Deposit amount input for deposit-bearing scheduling methods — the parent renders it when
 * `isDepositBearingSelection` (`schedulePreview.ts`) is true: the product's `allow_deposit`,
 * or a legacy `*_with_deposit` frozen template. react-hook-form + Zod (CLAUDE.md — no raw
 * `<input>` for entity forms); the parent only ever receives an already-validated,
 * already-debounced minor-unit amount, so it can feed it straight into
 * `mapMethodToParams`/`useSchedulePreview` without re-validating.
 *
 * Deliberately does NOT persist to Redux itself — matching the as-built
 * 475-B pattern (`PaymentStep`'s `selectedMethodKey`), the deposit stays
 * in-progress local state in the parent until the salesperson confirms the
 * schedule, at which point the current `params` (already carrying
 * `depositAmount`) is captured into `paymentSelection.params` as a whole.
 */
export const DepositInput: React.FC<DepositInputProps> = ({
    minDepositMinor,
    maxDepositMinor,
    maxDepositLabel,
    inclusiveMaxDisplayMinor,
    currency,
    valueMinor,
    onDebouncedChange,
    debounceMs = DEBOUNCE_MS,
}) => {
    const schema = useMemo(
        () =>
            buildDepositAmountSchema(
                minDepositMinor || 0,
                maxDepositMinor,
                currency,
                maxDepositLabel,
                inclusiveMaxDisplayMinor,
            ),
        [minDepositMinor, maxDepositMinor, currency, maxDepositLabel, inclusiveMaxDisplayMinor],
    );

    const {
        control,
        trigger,
        formState: { errors },
    } = useForm<DepositAmountFormData>({
        values: { depositAmount: valueMinor !== undefined ? valueMinor / 100 : undefined },
        resolver: zodResolver(schema),
        mode: 'onChange',
    });

    const lastEmittedRef = useRef<number | undefined>(valueMinor);
    const pendingEditRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const rejectedDraftRef = useRef<string | undefined>(undefined);
    const schemaRef = useRef(schema);
    const onDebouncedChangeRef = useRef(onDebouncedChange);
    schemaRef.current = schema;
    onDebouncedChangeRef.current = onDebouncedChange;

    const cancelPendingEdit = useCallback(() => {
        clearTimeout(pendingEditRef.current);
        pendingEditRef.current = undefined;
    }, []);

    useEffect(() => {
        if (valueMinor !== lastEmittedRef.current) {
            cancelPendingEdit();
            rejectedDraftRef.current = undefined;
            lastEmittedRef.current = valueMinor;
        }
    }, [valueMinor, cancelPendingEdit]);

    useEffect(() => cancelPendingEdit, [cancelPendingEdit]);

    const emit = useCallback((next: number | undefined) => {
        if (next !== lastEmittedRef.current) {
            lastEmittedRef.current = next;
            onDebouncedChangeRef.current(next);
        }
    }, []);

    const settleDraft = useCallback(
        (raw: string) => {
            const parsed = schemaRef.current.safeParse({ depositAmount: Number(raw) });
            rejectedDraftRef.current = parsed.success ? undefined : raw;
            if (parsed.success) {
                emit(toMinorUnits(parsed.data.depositAmount as number));
            }
        },
        [emit],
    );

    const handleUserEdit = useCallback(
        (raw: string) => {
            cancelPendingEdit();
            rejectedDraftRef.current = undefined;
            if (raw === '') {
                emit(undefined);
                return;
            }
            pendingEditRef.current = setTimeout(() => {
                pendingEditRef.current = undefined;
                settleDraft(raw);
            }, debounceMs);
        },
        [cancelPendingEdit, emit, settleDraft, debounceMs],
    );

    useEffect(() => {
        const draft = rejectedDraftRef.current;
        if (draft !== undefined && pendingEditRef.current === undefined) {
            settleDraft(draft);
            void trigger('depositAmount');
        }
    }, [schema, settleDraft, trigger]);

    const effectiveMin = minDepositMinor || 0;
    // The DISPLAYED upper bound (#621): the inclusive cap when one is in force,
    // otherwise the exclusive comparison value (quote total / first collection
    // target), unchanged from before.
    const maxDepositDisplayMinor = inclusiveMaxDisplayMinor ?? maxDepositMinor;
    const errorId = 'depositAmount-error';
    const hasError = !!errors.depositAmount;

    return (
        <div data-testid="deposit-input">
            <Label htmlFor="depositAmount">Deposit amount</Label>
            <Controller
                name="depositAmount"
                control={control}
                render={({ field }) => (
                    <Input
                        type="number"
                        id="depositAmount"
                        step={0.01}
                        min="0"
                        value={field.value ?? ''}
                        placeholder={`e.g. ${formatMinorAmount(effectiveMin, currency)}`}
                        error={hasError}
                        // The shared `Input`'s own `hint` renders a bare,
                        // unassociated <p> — fine for the informational
                        // hint, but a screen-reader user gets nothing for a
                        // validation error. When erroring, the message is
                        // rendered below via our own `role="alert"` element
                        // instead (associated by `aria-describedby`), so the
                        // built-in hint is left empty to avoid a duplicate.
                        hint={
                            hasError
                                ? undefined
                                : `Between ${formatMinorAmount(effectiveMin, currency)} and ${formatMinorAmount(maxDepositDisplayMinor, currency)}. Leave blank to split the balance evenly.`
                        }
                        aria-invalid={hasError}
                        aria-describedby={hasError ? errorId : undefined}
                        onChange={(event) => {
                            const raw = event.target.value;
                            field.onChange(raw === '' ? undefined : Number(raw));
                            handleUserEdit(raw);
                        }}
                        onBlur={field.onBlur}
                    />
                )}
            />
            {hasError && (
                <p id={errorId} role="alert" className="mt-1.5 text-xs text-mp-error">
                    {errors.depositAmount?.message}
                </p>
            )}
        </div>
    );
};
